import { useCallback, useEffect, useState } from "react";
import { Button, Divider, Drawer, Input, message, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { DislikeOutlined, LikeOutlined, SendOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist";
import { GlobalWorkerOptions } from "pdfjs-dist/build/pdf";

import config from "../../../config";
import { getCookie } from "../../../services/cookies";
import countrySettings from "../countrySettings.json";
import { useAnalytics } from "../../components/hooks/useAnalytics";

GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const { Title, Paragraph, Text, Link } = Typography;
const { TextArea } = Input;

// Constants
const PDF_RENDER_SCALE = 1.5;
const ECG_PROXY_URL = config.ecgProxyUrl;

const ECGInterpreter = ({
  source,
  getUrl,
  sourceFile,
  query,
  onAnalyzed,
  onStatusDetected,
  disabled,
  className = "m-5",
  tooltip = "Analizar ECG con IA",
}) => {
  const countryCode = getCookie("country_code") || "CL";
  const showEcgTags = countrySettings.ecgTags?.[countryCode] ?? false;

  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [lastImageFile, setLastImageFile] = useState(null);

  const [isAskSectionOpen, setIsAskSectionOpen] = useState(false);
  const [askValueSpanish, setAskValueSpanish] = useState("");
  const [isAsking, setIsAsking] = useState(false);

  const [ecgStatus, setEcgStatus] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const { trackEvent } = useAnalytics();

  const DEFAULT_INTERPRETATION_QUERY =
    "Provide a concise clinical-style interpretation of this ECG image (rate, rhythm, intervals, axis, notable abnormalities). Use clear bullet points.";

  const STATUS_QUERY =
    "Evaluate this ECG image strictly. " +
    "Consider it 'normal' ONLY if it shows regular sinus rhythm, normal axis, normal PR/QRS/QTc intervals, and NO signs of ischemia, blocks, or hypertrophy. " +
    "Consider it 'alterado' if you detect ANY abnormality, including but not limited to: arrhythmias, AV or bundle branch blocks, ST-segment deviations, T-wave inversions, pathological Q-waves, or ectopic beats. " +
    "Based on these explicit criteria, is this ECG normal or alterado? Answer ONLY with the exact word 'normal' or 'alterado'. Do not provide any other text or explanation.";

  const SUGGESTIONS = [
    { es: "¿Existe bloqueo bifascicular o trifascicular?", en: "Is there a bifascicular or trifascicular block?" },
    { es: "¿Existe bloqueo AV de segundo o tercer grado?", en: "Is there a second- or third-degree AV block?" },
    { es: "¿Existe síndrome de Brugada tipo II o III?", en: "Is there Brugada syndrome type II or III?" },
    { es: "¿Existe preexcitación o síndrome de Wolff-Parkinson-White?", en: "Is there pre-excitation or Wolff-Parkinson-White syndrome?" },
    { es: "¿Existen signos de infarto previo o isquemia?", en: "Are there signs of prior infarction or ischemia?" },
    { es: "¿Existe taquicardia paroxística supraventricular?", en: "Is there paroxysmal supraventricular tachycardia (PSVT)?" },
    { es: "¿Existe taquiarritmia?", en: "Is there a tachyarrhythmia?" },
    { es: "¿Existe arritmia completa por fibrilación auricular o flutter?", en: "Is there complete arrhythmia due to atrial fibrillation or flutter?" },
    { es: "¿Existe bloqueo completo de rama izquierda (BCRI)?", en: "Is there a complete left bundle branch block (LBBB)?" },
    { es: "¿Existe bradicardia extrema (menor de 40 lpm)?", en: "Is there extreme bradycardia (less than 40 bpm)?" },
    { es: "¿Existen extrasístoles ventriculares?", en: "Are there ventricular extrasystoles (PVCs)?" },
  ];

  const isBusy = isLoading || isAsking;

  const convertPdfToImage = useCallback(async (file) => {
    try {
      const fileURL = URL.createObjectURL(file);
      const pdf = await pdfjsLib.getDocument(fileURL).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) throw new Error("No se pudo crear contexto 2D");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("toBlob falló"))),
          "image/jpeg",
          0.7
        );
      });
      return new File([blob], "converted-pdf.jpg", { type: "image/jpeg" });
    } catch (error) {
      console.error("Error PDF → imagen:", error);
      message.error("No se pudo convertir el PDF a imagen.");
      throw error;
    }
  }, []);

  const fetchImageFromUrl = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (blob.type === "application/pdf") {
        const pdfFile = new File([blob], "document.pdf", { type: "application/pdf" });
        return await convertPdfToImage(pdfFile);
      } else {
        const ext = blob.type === "image/png" ? "png" : blob.type === "image/jpeg" ? "jpg" : "img";
        return new File([blob], `image.${ext}`, { type: blob.type || "image/jpeg" });
      }
    } catch (error) {
      console.error("Error fetch URL:", error);
      message.error("No se pudo descargar el archivo desde la URL.");
      throw error;
    }
  }, [convertPdfToImage]);

  const resolveInputToImageFile = useCallback(async () => {
    if (sourceFile) {
      if (sourceFile.type === "application/pdf") return await convertPdfToImage(sourceFile);
      return sourceFile;
    }
    if (source) {
      const url = await getUrl(source);
      return await fetchImageFromUrl(url);
    }
    throw new Error("Debes pasar source o sourceFile");
  }, [sourceFile, source, getUrl, convertPdfToImage, fetchImageFromUrl]);

  const analyzeECG = useCallback(async (image, overrideQueryEn, silent = false, isStatusCheck = false) => {
    try {
      if (isStatusCheck) {
        setIsStatusLoading(true);
      } else {
        setAskValueSpanish("");
      }

      const formData = new FormData();
      formData.append("file", image);
      formData.append("query", overrideQueryEn || query || DEFAULT_INTERPRETATION_QUERY);

      const response = await fetch(ECG_PROXY_URL, {
        method: "POST",
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      const resultText =
        (data && (data.result || data.text || data.message)) || "No analysis available";

      if (isStatusCheck) {
        const textLower = resultText.toLowerCase();
        const isAltered = textLower.includes("alterado") || textLower.includes("abnormal") || textLower.includes("not normal");
        const normalized = isAltered ? "alterado" : (textLower.includes("normal") ? "normal" : "alterado");
        setEcgStatus(normalized);
        onStatusDetected?.(normalized);
      } else {
        setAnalysis({
          query: data.query || overrideQueryEn || query || DEFAULT_INTERPRETATION_QUERY,
          filename: data.filename || image?.name || "ECG",
          result: resultText,
          result_en: data.result_en || null,
          raw: data,
        });
        setFeedbackStatus(null);
        if (!silent) setIsDrawerOpen(true);
        onAnalyzed?.(resultText, data);
        if (!silent) message.success("Análisis de ECG completado.");
      }
    } catch (error) {
      console.error("Error analizando ECG:", error);
      if (isStatusCheck) {
        setEcgStatus(null);
      } else {
        setAnalysis({
          query: overrideQueryEn || query || DEFAULT_INTERPRETATION_QUERY,
          filename: image?.name || "ECG",
          result: "Error analyzing the ECG image. Please try again.",
          result_en: null,
          raw: null,
        });
        setFeedbackStatus(null);
        if (!silent) {
          setIsDrawerOpen(true);
          message.error("Error al analizar el ECG.");
        }
        onAnalyzed?.("Error analyzing the ECG image. Please try again.", null);
      }
    } finally {
      if (isStatusCheck) setIsStatusLoading(false);
    }
  }, [onAnalyzed, onStatusDetected, query]);

  const handlePredictor = useCallback(async () => {
    try {
      setIsLoading(true);
      const imageFile = await resolveInputToImageFile();
      setLastImageFile(imageFile);
      await analyzeECG(imageFile);
      if (!ecgStatus) {
        await analyzeECG(imageFile, STATUS_QUERY, true, true);
      }
    } catch (error) {
      console.error("Error in handlePredictor:", error);
    } finally {
      setIsLoading(false);
    }
  }, [resolveInputToImageFile, analyzeECG, ecgStatus]);

  useEffect(() => {
    setAnalysis(null);
    setEcgStatus(null);
    setFeedbackStatus(null);
    setLastImageFile(null);
    setIsAskSectionOpen(false);
    setAskValueSpanish("");

    const initAnalysis = async () => {
      if (source || sourceFile) {
        try {
          setIsLoading(true);
          const imageFile = await resolveInputToImageFile();
          setLastImageFile(imageFile);
          
          analyzeECG(imageFile, STATUS_QUERY, true, true);
        } catch (error) {
          console.error("Error in auto-init analysis:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    initAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, sourceFile]);

  const handleFeedback = useCallback(
    (vote) => {
      if (!analysis || feedbackStatus || isBusy) return;
      try {
        const eventName = vote === "up" ? "ecg_ai_feedback_up" : "ecg_ai_feedback_down";
        trackEvent?.(eventName, {
          result: analysis.result,
          query: analysis.query,
        });
        setFeedbackStatus(vote);
        message.success("¡Gracias por tu feedback!");
      } catch (err) {
        console.error("Error enviando feedback:", err);
        message.error("No se pudo enviar el feedback.");
      }
    },
    [analysis, feedbackStatus, trackEvent, isBusy]
  );

  const translateToEnglish = useCallback(async (spanishText) => {
    const response = await fetch(`${config.doctorIaUrl}/translation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text: spanishText }),
    });
    const data = await response.json();
    return data.translated_text;
  }, []);

  const toEnglish = useCallback(async (spanishText) => {
    if (!spanishText) return "";
    try {
      if (translateToEnglish) {
        const out = await translateToEnglish(spanishText);
        return out || spanishText;
      }
      return spanishText;
    } catch {
      return spanishText;
    }
  }, [translateToEnglish]);

  const askAnother = useCallback(async (questionSpanishOrEnglish) => {
    try {
      setIsAsking(true);
      const queryEnglish = await toEnglish(questionSpanishOrEnglish);
      const image = lastImageFile || (await resolveInputToImageFile());
      if (!image) {
        message.error("No hay imagen para analizar.");
        return;
      }
      await analyzeECG(image, queryEnglish);
    } finally {
      setIsAsking(false);
    }
  }, [lastImageFile, resolveInputToImageFile, analyzeECG, toEnglish]);

  const renderFooter = () => (
    <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
        <span>Powered by </span>
        <Link
          href="https://arxiv.org/pdf/2410.19008"
          target="_blank"
          rel="noopener noreferrer"
        >
          PULSE
        </Link>
        <Divider type="vertical" />
        <span>Researched and models trained at The Ohio State University</span>
        <Divider type="vertical" />
        <span>Implemented and tested by </span>
        <Link
          href="https://www.cmtsalud.cl"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontWeight: 500 }}
        >
          CMT Salud
        </Link>
      </div>
      <small style={{ marginTop: '8px', display: 'block', color: '#888', textAlign: 'center' }}>
        Liu, R., Bai, Y., Yue, X., & Zhang, P. (2024). Teach Multimodal LLMs to Comprehend Electrocardiographic Images.
      </small>
    </div>
  );

  const AnalyzeButton = (
    <Space>
      <Button
        className={className}
        type="dashed"
        onClick={handlePredictor}
        shape="circle"
        loading={isLoading}
        disabled={disabled || isLoading || (!source && !sourceFile)}
        icon={
          <img
            src="https://cmtsalud-resources.s3.us-west-2.amazonaws.com/ai.svg"
            alt="AI Icon"
            style={{ width: "1.2em", height: "1.2em", verticalAlign: "middle" }}
          />
        }
        aria-label="Analizar ECG con IA"
      />
      {showEcgTags && isStatusLoading && <Spin size="small" />}
      {showEcgTags && ecgStatus && (
        <Tag color={ecgStatus === "normal" ? "green" : "red"} className="ml-2">
          {ecgStatus === "normal" ? "Normal" : "Alterado"}
        </Tag>
      )}
    </Space>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{AnalyzeButton}</Tooltip> : AnalyzeButton}

      <Drawer
        title="Interpretación de ECG con IA"
        placement="right"
        width={800}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        maskClosable={!isBusy}
        keyboard={!isBusy}
        extra={
          <Space>
            <Tooltip title={feedbackStatus ? "Gracias por tu feedback" : "Análisis correcto"}>
              <Button
                icon={<LikeOutlined />}
                onClick={() => handleFeedback("up")}
                disabled={!!feedbackStatus || isBusy}
              />
            </Tooltip>
            <Tooltip title={feedbackStatus ? "Gracias por tu feedback" : "Análisis incorrecto"}>
              <Button
                danger
                icon={<DislikeOutlined />}
                onClick={() => handleFeedback("down")}
                disabled={!!feedbackStatus || isBusy}
              />
            </Tooltip>
          </Space>
        }
        footer={renderFooter()}
      >
        <Spin spinning={isBusy}>
          {!analysis ? (
            <Text type="secondary">Aún no hay resultados.</Text>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>
                  Resultado (ES) 
                  {showEcgTags && ecgStatus && (
                    <Tag color={ecgStatus === "normal" ? "green" : "red"} style={{ marginLeft: 8 }}>
                      {ecgStatus === "normal" ? "Normal" : "Alterado"}
                    </Tag>
                  )}
                </Title>
                <Paragraph style={{ whiteSpace: "pre-wrap" }}>
                  {analysis.result || "Sin resultados."}
                </Paragraph>
              </div>

              <Divider />

              {!isAskSectionOpen && (
                <Paragraph>
                  <Link onClick={() => setIsAskSectionOpen(true)}>
                    ¿Quieres hacer una pregunta específica?
                  </Link>
                </Paragraph>
              )}

              {isAskSectionOpen && (
                <>
                  <Space size={[8, 8]} wrap>
                    {SUGGESTIONS.map((suggestion) => (
                      <Tag
                        key={suggestion.en}
                        color="geekblue"
                        style={{ cursor: "pointer", marginBottom: 0 }}
                        onClick={() => askAnother(suggestion.en)}
                      >
                        {suggestion.es}
                      </Tag>
                    ))}
                  </Space>

                  <div>
                    <Title level={5} style={{ marginBottom: 8, marginTop: 8 }}>
                      Escribe tu pregunta
                    </Title>
                    <Text type="secondary">
                      Puedes escribir en español; se enviará en inglés al modelo.
                    </Text>
                    <TextArea
                      rows={3}
                      style={{ marginTop: 8 }}
                      placeholder="Ej: ¿Hay signos de hipertrofia ventricular izquierda?"
                      value={askValueSpanish}
                      onChange={(event) => setAskValueSpanish(event.target.value)}
                      disabled={isBusy}
                    />
                    <div style={{ marginTop: 8, textAlign: "right" }}>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={isAsking}
                        disabled={!askValueSpanish?.trim() || isBusy}
                        onClick={() => askAnother(askValueSpanish.trim())}
                      >
                        Preguntar
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Space>
          )}
        </Spin>
      </Drawer>
    </>
  );
}

export default ECGInterpreter;